import type { FunctionComponent } from 'react'
import { useMemo } from 'react'
import type { LinksListExpandable } from '@which/seatbelt'
import { ButtonLink, ContentCardV2, Typography, useId } from '@which/seatbelt'
import { ExternalLinkIcon } from '@which/seatbelt/src/components/Icons/Navigational/ExternalLink'
import type { ImageSource } from '@which/seatbelt/src/types'
import { dynamicGa4DataLayerPush, isExternalLink } from '@which/shared'

import type { Link } from '../../../generated/frontend'
import styles from './ToolCta.module.scss'

export const ToolCTA: FunctionComponent<Props> = ({
  button,
  description,
  image,
  title,
  widgetImageAspectRatio,
  linksListExpandableData,
  captionText,
}) => {
  const uniqueId = useId()
  const contentHtmlId = useMemo(() => `tool-cta-links-list-${uniqueId}`, [uniqueId])
  const buttonProps: Link = { text: button?.text ?? '', href: button?.href ?? '' }
  const buttonLinkAdditionalProps = isExternalLink(buttonProps.href)
    ? { target: '_blank', rel: 'nofollow noopener noreferrer' }
    : {}

  const onClickTracking = () => {
    dynamicGa4DataLayerPush({
      ...sharedTrackingProps,
      item_text: button.text,
      item_parent_text: title,
      item_spec: 'cta card',
    })
  }

  const imageProps = {
    aspectRatioMain: getImageAspectRatio(widgetImageAspectRatio),
    imageAlt: image?.alt || '',
    imageSources: image?.sources,
    lazyLoad: false,
    str: image?.src || '',
  }

  const secondaryLinksProps = {
    ariaLabel: linksListExpandableData?.button.ariaLabel || '',
    buttonText: linksListExpandableData?.button.text || '',
    contentHtmlId,
    links: linksListExpandableData?.items || [],
  }

  return (
    <div data-testid="tool-cta" className={styles.toolCTA}>
      <ContentCardV2
        arrangement={'horizontal'}
        buttonLinkText={buttonProps.text}
        buttonLinkAdditionalProps={buttonLinkAdditionalProps}
        description={description}
        imageRight={true}
        imgObj={imageProps}
        primaryLink={buttonProps.href}
        secondaryLinks={secondaryLinksProps}
        title={title}
        legalText={captionText}
        titleTag="h3"
        onClickHandler={onClickTracking}
        clampDescText={false}
      />
    </div>
  )
}

export const ToolCTAWithHeroImage = ({
  title,
  description,
  button_text,
  button_url,
  caption_text,
}: WithHeroProps) => {
  if (!title || !button_text || !button_url) {
    return null
  }

  const onClickTracking = () => {
    dynamicGa4DataLayerPush({
      ...sharedTrackingProps,
      item_text: button_text,
      item_parent_text: title,
      item_spec: 'in image',
    })
  }

  return (
    <div className={styles.toolCTAWithHero} data-testid="tool-cta-hero">
      <Typography textStyle="sb-text-interface-heading-large" tag="h2">
        {title}
      </Typography>

      {description && (
        <Typography
          textStyle="sb-text-interface-body-default-regular"
          className={styles.description}
        >
          {description}
        </Typography>
      )}

      <ButtonLink
        href={button_url}
        className={styles.buttonLink}
        target="_blank"
        rel="nofollow noopener noreferrer"
        onClick={onClickTracking}
      >
        <ExternalLinkIcon />

        {button_text}
      </ButtonLink>

      {caption_text && (
        <Typography textStyle="sb-text-interface-body-small-regular" className={styles.disclaimer}>
          {caption_text}
        </Typography>
      )}
    </div>
  )
}

///////// IMPLEMENTATION /////////

const sharedTrackingProps = {
  event: 'click_cta',
  utagid: 'WHC873DP01',
  item_group: 'in article cta',
}

const getImageAspectRatio = (widgetImageAspectRatio: WidgetImageAspectRatio) =>
  ({
    ar_1to1_: 'one-to-one' as const,
    ar_2to1_: 'two-to-one' as const,
  })[widgetImageAspectRatio]

type WidgetImageAspectRatio = 'ar_1to1_' | 'ar_2to1_'

export type Props = {
  button: Link
  description?: string
  image?: {
    alt: string
    sources?: ImageSource[]
    src: string
  }
  linksListExpandableData?: Omit<Parameters<typeof LinksListExpandable>[0], 'contentHtmlId'>
  title: string
  widgetImageAspectRatio: WidgetImageAspectRatio
  captionText?: string
}

export type WithHeroProps = {
  title?: string
  description?: string
  button_text?: string
  button_url?: string
  caption_text?: string
}
